import { useEffect, useEffectEvent, useState } from "react"

const slides = [
  {
    image:
      "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=2400&q=100",
    alt: "Modern interior living room",
    eyebrow: "Signature Home Interior",
    title: ["Urban", "Space"],
    description:
      "Refined residential interiors shaped through texture, light, and tailored spatial flow.",
  },
  {
    image:
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=2400&q=100",
    alt: "Modern kitchen interior",
    eyebrow: "Kitchen & Dining Vision",
    title: ["Warm", "Geometry"],
    description:
      "Material-led kitchen environments balancing clean lines, warmth, and daily functionality.",
  },
  {
    image:
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2400&q=100",
    alt: "Elegant dining room",
    eyebrow: "Complete Home Styling",
    title: ["Quiet", "Luxury"],
    description:
      "Composed spaces with crafted finishes, layered lighting, and details that feel lived-in.",
  },
  {
    image:
      "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=2400&q=100",
    alt: "Bedroom interior design",
    eyebrow: "Personalized Room Design",
    title: ["Soft", "Statements"],
    description:
      "Bedrooms and private zones designed for calm, intimacy, and a stronger architectural mood.",
  },
]

function Arrow({
  direction,
}: {
  direction: "left" | "right"
}) {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      {direction === "left" ? (
        <path d="M15 19l-7-7 7-7" />
      ) : (
        <path d="M9 5l7 7-7 7" />
      )}
    </svg>
  )
}

export function Hero() {
  const [activeIndex, setActiveIndex] = useState(0)
  const activeSlide = slides[activeIndex]

  const advanceSlide = useEffectEvent(() => {
    setActiveIndex((current) => (current + 1) % slides.length)
  })

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      advanceSlide()
    }, 5500)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [])

  const goToPrevious = () => {
    setActiveIndex((current) => (current - 1 + slides.length) % slides.length)
  }

  const goToNext = () => {
    setActiveIndex((current) => (current + 1) % slides.length)
  }

  return (
    <section id="home" className="pt-[73px]">
      <div className="w-full pb-8">
        <div className="relative min-h-[620px] overflow-hidden bg-gray-100 md:min-h-[calc(100vh-73px)]">
          {slides.map((slide, index) => (
            <div
              key={slide.image}
              className={`absolute inset-0 transition-opacity duration-[1400ms] ease-out ${
                activeIndex === index ? "opacity-100" : "opacity-0"
              }`}
              aria-hidden={activeIndex !== index}
            >
              <img
                src={slide.image}
                alt={slide.alt}
                className={`h-full w-full object-cover transition-transform duration-[7000ms] ease-out ${
                  activeIndex === index ? "scale-100" : "scale-110"
                }`}
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(to bottom, rgba(10, 22, 40, 0.45) 0%, rgba(10, 22, 40, 0.55) 100%)",
                }}
              />
            </div>
          ))}

          <div className="absolute inset-0 flex items-center justify-center px-6 py-10 md:px-12">
            <div className="relative w-full max-w-6xl text-white">
              <div className="pointer-events-none absolute inset-0 border border-white/12" />

              <div
                key={activeIndex}
                className="animate-in fade-in slide-in-from-bottom-6 relative px-8 pt-12 pb-24 duration-700 md:px-16 md:pt-18 md:pb-32"
              >
                <div className="inline-flex items-center gap-3 border border-white/20 bg-white/10 px-4 py-2 backdrop-blur-sm">
                  <span className="h-1.5 w-1.5 rounded-full bg-white/80" />
                  <span className="text-[11px] font-light tracking-[0.35em] uppercase">
                    {activeSlide.eyebrow}
                  </span>
                </div>

                <div className="mt-8 grid gap-10 md:grid-cols-[1.3fr_0.7fr] md:items-end">
                  <div>
                    <h1 className="font-heading text-5xl leading-none tracking-[0.04em] sm:text-6xl md:text-8xl">
                      <span className="block">{activeSlide.title[0]}</span>
                      <span className="mt-2 block text-white/75">
                        {activeSlide.title[1]}
                      </span>
                    </h1>
                  </div>

                  <div className="md:justify-self-end md:text-right">
                    <p className="max-w-sm text-sm font-light leading-relaxed tracking-[0.12em] text-white/80 uppercase">
                      {activeSlide.description}
                    </p>

                    <div className="mt-8 flex items-center gap-4 md:justify-end">
                      <span className="h-px w-16 bg-white/40" />
                      <span className="text-[11px] tracking-[0.3em] text-white/70 uppercase">
                        0{activeIndex + 1}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute right-6 bottom-6 left-6 flex items-end justify-between gap-6 md:right-8 md:bottom-8 md:left-8">
                <div className="flex items-center gap-2">
                  {slides.map((slide, index) => (
                    <button
                      key={slide.image}
                      type="button"
                      aria-label={`Go to slide ${index + 1}`}
                      aria-pressed={activeIndex === index}
                      onClick={() => setActiveIndex(index)}
                      className={`h-[2px] transition-all duration-500 ${
                        activeIndex === index ? "w-14 bg-white" : "w-6 bg-white/35"
                      }`}
                    />
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={goToPrevious}
                    aria-label="Previous slide"
                    className="flex h-11 w-11 items-center justify-center border border-white/20 bg-white/10 text-white backdrop-blur-sm transition-all duration-300 hover:bg-white hover:text-navy"
                  >
                    <Arrow direction="left" />
                  </button>
                  <button
                    type="button"
                    onClick={goToNext}
                    aria-label="Next slide"
                    className="flex h-11 w-11 items-center justify-center border border-white/20 bg-white/10 text-white backdrop-blur-sm transition-all duration-300 hover:bg-white hover:text-navy"
                  >
                    <Arrow direction="right" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
